import { GetServerSideProps, NextPage } from "next";
import { getServerSession } from "next-auth";
import React from "react";

import { searchArticle, SearchArticleKey } from "../../components/ArticleList";
import { getBookmarkArticles } from "../../components/BookmarkButton/query";
import { getLikeArticles } from "../../components/LikeButton/query";
import { DefaultPageProps, getCommonServerSideProps } from "../../lib/ssrProps";
import Template, { getArticle, GetArticleKey } from "../../templates/article";
import { ApiDataNotFoundError } from "../../utils";
import { isAuthenticated } from "../../utils/auth";
import { isUUID } from "../../utils/uuid";
import { authOptions } from "../api/auth/[...nextauth]";

type Props = {
  bookmarkIds: string[];
  id: string;
  keyword: string;
  isAuthenticated: boolean;
  likeIds: string[];
  fullURL: string;
} & DefaultPageProps;

const ArticlePage: NextPage<Props> = ({
  bookmarkIds,
  id,
  keyword,
  isAuthenticated,
  likeIds,
  fullURL,
}) => {
  return (
    <Template
      bookmarkIds={bookmarkIds}
      id={id}
      keyword={keyword}
      isAuthenticated={isAuthenticated}
      likeIds={likeIds}
      fullURL={fullURL}
    />
  );
};

export const getServerSideProps: GetServerSideProps<
  {},
  { id: string }
> = async (context) =>
  getCommonServerSideProps(context, async (context) => {
    const keyword = context.params?.id;
    if (!keyword) {
      return {
        notFound: true,
      };
    }

    const session = await getServerSession(
      context.req,
      context.res,
      authOptions,
    );

    try {
      const article = await getArticle({ key: decodeURIComponent(keyword) });

      if (isUUID(keyword) && article.urlKeyword !== null) {
        return {
          redirect: {
            permanent: true,
            destination: "/articles/" + encodeURIComponent(article.urlKeyword),
          },
        };
      }

      const relatedArticles = await searchArticle({
        excludeIds: [article.id],
        limit: 3,
        tagIds: article.tags[0]?.id ? [article.tags[0].id] : undefined,
      });

      const fullURL = `${process.env.APP_HOST || context.req.headers.host}/articles/${keyword}`;

      const [bookmarkIds, likeIds] = session?.accessToken
        ? await Promise.all([
            getBookmarkArticles({ session }),
            getLikeArticles({ session }),
          ])
        : [[], []];

      return {
        props: {
          bookmarkIds,
          id: article.id,
          keyword,
          likeIds,
          fullURL,
          isAuthenticated: isAuthenticated(session),
          fallback: {
            [GetArticleKey(keyword)]: article,
            [SearchArticleKey({
              excludeIds: [article.id],
              limit: 3,
              page: 1,
              tagIds: article.tags[0]?.id ? [article.tags[0].id] : undefined,
            })]: relatedArticles,
          },
        },
      };
    } catch (e) {
      if (e instanceof ApiDataNotFoundError)
        return {
          notFound: true,
        };
      else throw e;
    }
  });

export default ArticlePage;
